import { appErrorService } from '@/services/AppErrorService'

import { ProofTypeId } from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'
import type { AttributeDto } from '@VelocitySquared/sunny16-commerce-dataobjects/shared'
import type { CatalogBundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

export const PROOF_TYPE_ATTRIBUTE_KEY = 'CADY_ProofType'

// Per Brian's 2026-06-05 clarification, the storefront accepts any `ProofTypeId`
// enum member name as a `CADY_ProofType` attribute value — the value vocabulary
// IS the enum. Numeric enums get a string-to-number reverse lookup from
// TypeScript at runtime; the `typeof === 'number'` guard rejects the
// number-to-string direction (e.g. attribute value `"2"` → `"Portfolio"`) along
// with anything not in the enum.
function parseProofTypeAttributeValue(value: string): ProofTypeId | null {
  const mapped = (ProofTypeId as Record<string, unknown>)[value]
  return typeof mapped === 'number' ? (mapped as ProofTypeId) : null
}

const reportedUnknownProofTypeValues = new Set<string>()

function reportUnknownProofTypeAttributeValueOnce(value: string): void {
  if (reportedUnknownProofTypeValues.has(value)) return
  reportedUnknownProofTypeValues.add(value)
  appErrorService.reportError(new Error(`Unknown ${PROOF_TYPE_ATTRIBUTE_KEY} attribute value`), {
    value
  })
}

/**
 * Core proof-type applicability check over a generic `AttributeDto` collection — shared by catalog
 * bundles and reward promotions (anything carrying `CADY_ProofType` attributes). Returns true
 * (universal) when no proof-type attributes are authored, so un-scoped items apply to everyone.
 */
export function isApplicableToProofType(
  attributes: AttributeDto[] | null | undefined,
  subjectProofType: ProofTypeId | null
): boolean {
  const proofTypeAttributes = (attributes ?? []).filter((a) => a.key === PROOF_TYPE_ATTRIBUTE_KEY)
  if (proofTypeAttributes.length === 0) return true

  const allowed = new Set<ProofTypeId>()
  for (const attribute of proofTypeAttributes) {
    const mapped = parseProofTypeAttributeValue(attribute.value)
    if (mapped === null) {
      reportUnknownProofTypeAttributeValueOnce(attribute.value)
      continue
    }
    allowed.add(mapped)
  }

  // Every authored value was unparseable — degrade to universal so a typo
  // doesn't hide the item from everyone. The unknown values were already
  // reported above.
  if (allowed.size === 0) return true

  if (subjectProofType === null) return false

  return allowed.has(subjectProofType)
}

export function isCatalogBundleApplicableToProofType(
  bundle: CatalogBundleStorefrontDto,
  subjectProofType: ProofTypeId | null
): boolean {
  return isApplicableToProofType(bundle.attributes, subjectProofType)
}
